import React, { PureComponent, Fragment } from 'react';
import PropTypes from 'prop-types';
import { Modal } from 'react-bootstrap';
import { Image, Select, Loader } from '@components/common';
import { PaymentService } from '@services';
import utils, { constants } from '@utils';
import { AddPaymentMethodModal } from '../../../../../components';

import './PaymentPreviewModal.scss';

class PaymentPreviewModal extends PureComponent {
	constructor(props) {
		super(props);

		this.state = {
			show               : false,
			total              : 0,
			defaultCard        : {},
			showBillingMessage : false,
			paymentMethods     : [],
			paymentMethod      : {},
			showPayment        : false,
			loading            : false,
			loadingPM          : false,
		};
	}

	openModal = ({
		total, defaultCard, showBillingMessage, paymentMethods,
	}) => {
		const paymentMethod = {};
		const defaultMethod = paymentMethods.find((pm) => pm.isDefault);
		if (defaultMethod) {
			const {
				cardType,
				bankName,
				cardNumber,
				accountNumber,
			} = defaultMethod;
			paymentMethod.value = defaultMethod.id;
			paymentMethod.label = `${cardType ? cardType.charAt(0).toUpperCase() + cardType.substr(1) : bankName} ending in ${cardNumber || accountNumber}`;
		}
		this.setState({
			show : true,
			total,
			defaultCard,
			showBillingMessage,
			paymentMethods,
			paymentMethod,
		});
	}

	getPaymentMethods = () => {
		this.setState({ loadingPM : true });
		PaymentService.getData().then(({ data }) => {
			if (data && ((data.PaymentCards && data.PaymentCards.length > 0) || (data.BankAccounts && data.BankAccounts.length > 0))) {
				const bankAccounts = (data.BankAccounts || []).map((account) => ({
					id            : account.BankAccountId,
					type          : 'bank_account',
					isDefault     : account.IsDefaultCard,
					bankName      : account.BankName,
					accountNumber : account.AccountNumber.substr(account.AccountNumber.lastIndexOf('*') + 1),
					accountStatus : account.BankAccountStatus,
				}));
				const cards = (data.PaymentCards || []).map((card) => ({
					id         : card.PaymentCardId,
					type       : 'card',
					isDefault  : card.IsDefaultCard,
					cardNumber : card.CardNumber.replace(/[.]/g, '*'),
					cardType   : card.CardType.replace(' ', '-').toLowerCase(),
					expiry     : `${card.ExpirationMonth.toString().length === 1
						? `0${card.ExpirationMonth}`
						: card.ExpirationMonth}/${card.ExpirationYear.toString().substr(2)}`,
				}));
				const paymentMethods = [...bankAccounts, ...cards];
				const defaultCard = paymentMethods.find((item) => item.isDefault);
				this.setState({
					paymentMethods,
					defaultCard,
					loadingPM : false,
				});
			}
		}).catch(() => {
			utils.notify({
				type    : 'error',
				message : 'Error retrieving payment methods',
			});
		});
	}

	onClose = () => {
		this.setState({
			show               : false,
			total              : 0,
			defaultCard        : {},
			showBillingMessage : false,
			paymentMethods     : [],
			paymentMethod      : {},
			showPayment        : false,
			loading            : false,
		});
	}

	onPayClick = () => {
		const { onPay } = this.props;
		this.setState({ show : false }, onPay);
	}

	onAddPaymentMethod = () => {
		const { showAddPaymentModal } = this.props;
		this.onClose();
		showAddPaymentModal();
	};

	changePaymentMethod = (cardId) => {
		this.setState({ loading : true });
		PaymentService.setPreferredCard(cardId)
			.then(() => {
				this.setState({ showPayment : false, loading : false }, () => {
					this.getPaymentMethods();
				});
			}).catch(() => {
				this.setState({ loading : false });
				utils.notify({
					type    : 'error',
					message : 'Error setting up preferred card',
				});
			});
	}

	onPaymentMethodChange = (e) => {
		const value = e ? e.value : null;
		const label = e ? e.label : null;
		this.setState({ paymentMethod : { value, label } });
	}

	render() {
		const {
			show,
			total,
			defaultCard,
			showBillingMessage,
			paymentMethod,
			paymentMethods,
			showPayment,
			loading,
			loadingPM,
		} = this.state;

		const { achPrePayEnabled, user, callback } = this.props;
		const pm = paymentMethods.map(({
			id,
			type,
			bankName,
			cardType,
			accountNumber,
			cardNumber,
		}) => {
			const result = { value : id };
			if (type === 'card') {
				const capitalizedCardType = cardType.charAt(0).toUpperCase() + cardType.substr(1);
				result.label = `${capitalizedCardType} ending in ${cardNumber}`;
			} else {
				result.label = `${bankName} ending in ${accountNumber}`;
			}
			return result;
		});

		return (
			<Fragment>
				<Modal show={show} onHide={this.onClose} id="payment-preview-modal">
					<Modal.Header closeButton>
						<Modal.Title>
							<div id="payment-preview-total">
								{utils.currencyFormatter(total)}
							</div>
							<div id="payment-preview-amount-message">
								Total amount charged to you
							</div>
							<If condition={showBillingMessage}>
								<div id="payment-preview-billing-message">
									&#42;The first payment will be for 2 cycles and then will be billed on a weekly cycle
								</div>
							</If>
						</Modal.Title>
					</Modal.Header>
					<Modal.Body>
						<h4 className="text-center">Payment Method</h4>
						<div className="payment-method-summary row">
							<If condition={!showPayment}>
								<Choose>
									<When condition={achPrePayEnabled && defaultCard.type === 'bank_account'}>
										<div className="col-xs-12 text-center">
											{`Your ${defaultCard.bankName} ending in ${defaultCard.accountNumber} will be charged`}
										</div>
									</When>
									<When condition={defaultCard.type === 'card'}>
										<div className="col-xs-12 text-center">
											<Image
												className="payment-methods-card-image"
												src={constants.CARD_ICONS[defaultCard.cardType] || constants.CARD_ICONS.default}
											/>
											<span>{` ending in ${defaultCard.cardNumber} will be charged`}</span>
										</div>
									</When>
								</Choose>
							</If>
							<If condition={showPayment}>
								<div className="col-xs-12 col-sm-7 col-sm-offset-1">
									<Select
										value={paymentMethod.value}
										name="payment-method"
										onChange={this.onPaymentMethodChange}
										options={pm}
									/>
								</div>
								<div className="col-xs-12 col-sm-4 btn-container form-group">
									<button
										type="button"
										className="btn btn-link"
										onClick={() => this.setState({ showPayment : false, paymentMethod : {} })}
									>
										Cancel
									</button>
									<button
										type="button"
										className="btn btn-primary"
										onClick={() => this.changePaymentMethod(paymentMethod.value)}
										disabled={Object.keys(paymentMethod).length === 0 || loading}
									>
										Save
									</button>
								</div>
								<div className="col-xs-12 text-center">
									<button
										type="button"
										className="btn btn-link"
										onClick={this.onAddPaymentMethod}
									>
										Add new payment method
									</button>
								</div>
							</If>
						</div>
						<If condition={!achPrePayEnabled && defaultCard.type === 'bank_account' && !showPayment}>
							<div className="text-center">ACH unavailable due to required processing time (5 business days).</div>
							<div className="text-center">Please select a different payment method.</div>
						</If>
						<Loader active={loadingPM} size="small" />
						<If condition={!showPayment}>
							<div className="text-center">
								<button
									type="button"
									className="btn btn-link text-center"
									onClick={() => this.setState({ showPayment : true })}
								>
									Change default payment method
								</button>
							</div>
						</If>
					</Modal.Body>
					<If condition={achPrePayEnabled || defaultCard.type !== 'bank_account'}>
						<Modal.Footer>
							<button
								type="button"
								className="btn btn-primary"
								onClick={this.onPayClick}
							>
								Pay Now
							</button>
						</Modal.Footer>
					</If>
				</Modal>
				<AddPaymentMethodModal
					user={user}
					callback={callback}
					achPrePayEnabled={achPrePayEnabled}
				/>
			</Fragment>
		);
	}
}

PaymentPreviewModal.propTypes = {
	onPay               : PropTypes.func.isRequired,
	achPrePayEnabled    : PropTypes.bool,
	user                : PropTypes.object,
	callback            : PropTypes.func,
	showAddPaymentModal : PropTypes.func,
};

PaymentPreviewModal.defaultProps = {
	achPrePayEnabled    : false,
	user                : {},
	callback            : () => {},
	showAddPaymentModal : () => {},
};

export default PaymentPreviewModal;
